import { useState } from 'react';

import { SoftCard } from '@/components/ui/soft-card';
import { cn } from '@/lib/utils';

/**
 * Semantic-table variant of ColumnTable, styled to match the Inventory
 * "manage items" table (header band, row dividers, hover) with a client-side
 * pagination footer (Prev / page badge / Next). `columns` uses the same
 * `{ key, label, width?, render? }` shape as ColumnTable so a config can drive
 * both the summary card and this full-list view.
 *
 * Pagination is client-side: the caller passes the full (already server-capped)
 * array and we slice it per page. The footer only renders when there's more
 * than one page.
 */
export function PaginatedTable({ rows, columns, emptyLabel, pageSize = 10 }) {
	const [page, setPage] = useState(1);

	const total = rows.length;
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const current = Math.min(page, totalPages);
	const start = (current - 1) * pageSize;
	const pageRows = rows.slice(start, start + pageSize);
	const lastIdx = columns.length - 1;

	// The Prev/Next controls sit at the bottom of a tall table, so paging
	// without this leaves the user scrolled to the bottom of the new page.
	// Jump straight to the top — same call AppLayout uses on route change
	// (instant, not a smooth animation; matches the manage-items table, whose
	// server-side reload collapses the page height to the top on page change).
	const goToPage = (next) => {
		setPage(next);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
		}
	};

	return (
		<SoftCard className="overflow-hidden !p-0">
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-border/60 bg-muted/30 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
							{columns.map((c, i) => (
								<th
									key={c.key}
									className={cn('px-5 py-2.5 font-semibold', i !== 0 && 'text-right')}
									style={c.width ? { width: c.width } : undefined}
								>
									{c.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-border/60">
						{total === 0 ? (
							<tr>
								<td colSpan={columns.length} className="px-5 py-12 text-center text-sm text-muted-foreground">
									{emptyLabel ?? 'No data yet.'}
								</td>
							</tr>
						) : (
							pageRows.map((row, i) => (
								<tr key={row.id ?? start + i} className="transition hover:bg-muted/40">
									{columns.map((c, idx) => (
										<td
											key={c.key}
											className={cn(
												'px-5 py-3',
												idx === 0 ? 'font-medium text-foreground' : 'text-right tabular-nums text-foreground',
												idx === lastIdx && idx !== 0 && 'font-semibold'
											)}
										>
											{c.render ? c.render(row) : row[c.key]}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			{total > pageSize ? (
				<div className="flex items-center justify-between border-t border-border/60 px-5 py-3 text-xs text-muted-foreground">
					<span className="tabular-nums">
						Showing {start + 1}–{Math.min(start + pageSize, total)} of {total.toLocaleString()}
					</span>
					<div className="flex items-center gap-1">
						<button
							type="button"
							className="rounded-md px-2.5 py-1 transition hover:bg-accent disabled:opacity-40"
							disabled={current <= 1}
							onClick={() => goToPage(Math.max(1, current - 1))}
						>
							Prev
						</button>
						<span className="rounded-md bg-foreground px-2.5 py-1 font-semibold text-background tabular-nums">{current}</span>
						<button
							type="button"
							className="rounded-md px-2.5 py-1 transition hover:bg-accent disabled:opacity-40"
							disabled={current >= totalPages}
							onClick={() => goToPage(Math.min(totalPages, current + 1))}
						>
							Next
						</button>
					</div>
				</div>
			) : null}
		</SoftCard>
	);
}
